/**
 * WebRTC Service — Browser Call Client
 *
 * Hybrid approach:
 * - WebRTC (RTCPeerConnection) for receiving TTS audio from server
 * - WebSocket for sending mic audio to server (bypasses werift RTP receive issues)
 *
 * The signaling WebSocket handles auth, SDP negotiation, ICE candidates,
 * and metadata (transcripts, barge-in, state).
 *
 * Usage:
 *   const call = new BrowserCall({ agentId, token, ujid, wsUrl });
 *   call.addEventListener('state', e => ...);      // 'connecting', 'active', 'ended'
 *   call.addEventListener('transcript', e => ...);  // { speaker, text }
 *   call.addEventListener('error', e => ...);
 *   await call.start();
 *   call.stop();
 */

// ── Mulaw encoding (PCM float32 → G.711 µ-law) ──
const MULAW_BIAS = 0x84;
const MULAW_MAX = 0x7FFF;
const MULAW_CLIP = 32635;

function float32ToMulaw(sample) {
  // Convert float [-1, 1] to 16-bit signed integer
  let pcm = Math.max(-1, Math.min(1, sample)) * 32767;
  const sign = pcm < 0 ? 0x80 : 0;
  if (pcm < 0) pcm = -pcm;
  if (pcm > MULAW_CLIP) pcm = MULAW_CLIP;
  pcm += MULAW_BIAS;

  let exponent = 7;
  for (let expMask = 0x4000; (pcm & expMask) === 0 && exponent > 0; exponent--, expMask >>= 1) {}
  const mantissa = (pcm >> (exponent + 3)) & 0x0F;
  return ~(sign | (exponent << 4) | mantissa) & 0xFF;
}

function downsampleAndEncode(inputBuffer, inputSampleRate, outputSampleRate) {
  const ratio = inputSampleRate / outputSampleRate;
  const outputLength = Math.floor(inputBuffer.length / ratio);
  const output = new Uint8Array(outputLength);

  for (let i = 0; i < outputLength; i++) {
    // Linear interpolation for smoother downsampling
    const srcPos = i * ratio;
    const srcIndex = Math.floor(srcPos);
    const frac = srcPos - srcIndex;
    const s0 = inputBuffer[srcIndex] || 0;
    const s1 = inputBuffer[Math.min(srcIndex + 1, inputBuffer.length - 1)] || 0;
    const sample = s0 + (s1 - s0) * frac;
    output[i] = float32ToMulaw(sample);
  }
  return output;
}

// Base64 encode Uint8Array
function uint8ToBase64(bytes) {
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}


export class BrowserCall extends EventTarget {
    constructor({ agentId, token, ujid, wsUrl }) {
        super();
        this.agentId = agentId;
        this.token = token;
        this.ujid = ujid;
        this.wsUrl = wsUrl;

        this.ws = null;            // Signaling WebSocket
        this.pc = null;            // RTCPeerConnection (for receiving TTS only)
        this.mediaStream = null;   // Local mic stream
        this.remoteAudio = null;   // <audio> element for TTS playback
        this.audioContext = null;   // AudioContext for mic capture
        this.scriptNode = null;    // ScriptProcessorNode for mic → mulaw
        this.callId = null;
        this.state = 'idle';       // idle, connecting, active, ended
    }

    /**
     * Start the browser call — request microphone, connect signaling,
     * negotiate WebRTC for TTS, start mic capture via WebSocket.
     */
    async start() {
        try {
            this._setState('connecting');

            // Request microphone with echo cancellation
            this.mediaStream = await navigator.mediaDevices.getUserMedia({
                audio: {
                    echoCancellation: true,
                    noiseSuppression: true,
                    autoGainControl: true
                }
            });

            // Create hidden <audio> element for TTS playback
            this.remoteAudio = document.createElement('audio');
            this.remoteAudio.autoplay = true;

            // Connect signaling WebSocket
            this.ws = new WebSocket(this.wsUrl);

            this.ws.onopen = () => {
                // Send start message to authenticate and init call
                this.ws.send(JSON.stringify({
                    type: 'start',
                    agentId: this.agentId,
                    token: this.token,
                    ujid: this.ujid
                }));
            };

            this.ws.onmessage = (event) => this._handleSignalingMessage(event);

            this.ws.onerror = (error) => {
                console.error('Signaling WebSocket error:', error);
                this._emit('error', 'Connection error');
            };

            this.ws.onclose = () => {
                if (this.state === 'active' || this.state === 'connecting') {
                    this._setState('ended');
                    this._emit('ended', { reason: 'disconnected' });
                }
            };

        } catch (error) {
            this._setState('ended');
            this._emit('error', error.message);
            this._cleanup();
            throw error;
        }
    }

    /**
     * Stop the call gracefully.
     */
    stop() {
        if (this.ws && this.ws.readyState === WebSocket.OPEN) {
            try {
                this.ws.send(JSON.stringify({ type: 'stop' }));
            } catch (e) { /* ignore */ }
        }

        this._cleanup();
        this._setState('ended');
    }

    /**
     * Handle signaling messages from the server.
     */
    _handleSignalingMessage(event) {
        try {
            const msg = JSON.parse(event.data);

            switch (msg.type) {
                case 'sdp-offer':
                    this._handleSdpOffer(msg.sdp);
                    break;

                case 'ice-candidate':
                    this._handleIceCandidate(msg.candidate);
                    break;

                case 'ready':
                    this.callId = msg.callId;
                    this._setState('active');
                    this._startMicCapture();
                    this._emit('ready', { callId: msg.callId });
                    break;

                case 'audio':
                    // WebSocket audio fallback — ignore, we play via WebRTC track
                    break;

                case 'clear':
                    this._handleClear();
                    break;

                case 'transcript':
                    this._emit('transcript', {
                        speaker: msg.speaker,
                        text: msg.text
                    });
                    break;

                case 'state':
                    this._emit('pipelineState', { state: msg.state });
                    break;

                case 'hangup':
                    this._emit('hangup', { reason: msg.reason });
                    this._cleanup();
                    this._setState('ended');
                    break;

                case 'error':
                    this._emit('error', msg.message);
                    break;
            }
        } catch (error) {
            console.error('Failed to parse signaling message:', error);
        }
    }

    /**
     * Handle SDP offer from server.
     * Create RTCPeerConnection for receiving TTS audio.
     * Mic audio goes via WebSocket instead.
     */
    async _handleSdpOffer(sdp) {
        try {
            // Create peer connection
            this.pc = new RTCPeerConnection({
                iceServers: [
                    { urls: 'stun:stun.l.google.com:19302' },
                    { urls: 'stun:stun1.l.google.com:19302' }
                ]
            });

            // Send ICE candidates to server
            this.pc.onicecandidate = (event) => {
                if (event.candidate && this.ws?.readyState === WebSocket.OPEN) {
                    this.ws.send(JSON.stringify({
                        type: 'ice-candidate',
                        candidate: event.candidate.toJSON()
                    }));
                }
            };

            // Handle remote audio track (TTS from server)
            this.pc.ontrack = (event) => {
                if (event.streams && event.streams[0]) {
                    this.remoteAudio.srcObject = event.streams[0];
                } else if (event.track) {
                    const stream = new MediaStream([event.track]);
                    this.remoteAudio.srcObject = stream;
                }
                // Ensure playback starts (Chrome autoplay policy)
                this.remoteAudio.play().catch(() => {});
            };

            // Monitor connection state
            this.pc.onconnectionstatechange = () => {
                const state = this.pc?.connectionState;
                if (state === 'failed') {
                    this._emit('error', 'WebRTC connection failed');
                }
            };

            // Set remote description (server's offer)
            await this.pc.setRemoteDescription(new RTCSessionDescription(sdp));

            // Still add mic track to WebRTC so the SDP answer includes sendrecv
            // (server expects bidirectional transceiver), but actual audio goes via WS
            this.mediaStream.getAudioTracks().forEach(track => {
                this.pc.addTrack(track, this.mediaStream);
            });

            // Create and send answer
            const answer = await this.pc.createAnswer();
            await this.pc.setLocalDescription(answer);

            this.ws.send(JSON.stringify({
                type: 'sdp-answer',
                sdp: this.pc.localDescription.toJSON()
            }));

        } catch (error) {
            console.error('WebRTC negotiation failed:', error);
            this._emit('error', 'WebRTC setup failed: ' + error.message);
        }
    }

    /**
     * Start capturing mic audio and sending via WebSocket.
     * Downsamples from browser sample rate (typically 48kHz) to 8kHz mulaw.
     * Sends 20ms chunks (160 bytes at 8kHz) as base64.
     */
    _startMicCapture() {
        if (!this.mediaStream || !this.ws) return;

        try {
            this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
            const sampleRate = this.audioContext.sampleRate;

            const source = this.audioContext.createMediaStreamSource(this.mediaStream);

            // Cascaded anti-aliasing low-pass filters at 3.5kHz (Nyquist for 8kHz target)
            // Two stages give -24dB/octave rolloff vs -12dB/octave for a single filter,
            // preventing aliasing artifacts from folding into the 0-4kHz speech band.
            const lpFilter1 = this.audioContext.createBiquadFilter();
            lpFilter1.type = 'lowpass';
            lpFilter1.frequency.value = 3500;
            lpFilter1.Q.value = 0.707;

            const lpFilter2 = this.audioContext.createBiquadFilter();
            lpFilter2.type = 'lowpass';
            lpFilter2.frequency.value = 3500;
            lpFilter2.Q.value = 0.707;

            // ScriptProcessorNode: 2048 samples buffer for ~42ms at 48kHz
            // Smaller buffer = more frequent chunks, closer to real-time STT
            this.scriptNode = this.audioContext.createScriptProcessor(2048, 1, 1);

            this.scriptNode.onaudioprocess = (e) => {
                if (this.state !== 'active' || !this.ws || this.ws.readyState !== WebSocket.OPEN) return;

                const inputData = e.inputBuffer.getChannelData(0);

                // Downsample to 8kHz and encode as mulaw
                const mulawData = downsampleAndEncode(inputData, sampleRate, 8000);

                // Send as base64 via WebSocket
                if (mulawData.length > 0) {
                    this.ws.send(JSON.stringify({
                        type: 'audio',
                        payload: uint8ToBase64(mulawData)
                    }));
                }
            };

            // Silent sink — keeps ScriptProcessorNode alive without outputting audio
            // (connecting to destination would leak processed audio to speakers)
            this._silentGain = this.audioContext.createGain();
            this._silentGain.gain.value = 0;

            source.connect(lpFilter1);
            lpFilter1.connect(lpFilter2);
            lpFilter2.connect(this.scriptNode);
            this.scriptNode.connect(this._silentGain);
            this._silentGain.connect(this.audioContext.destination);

        } catch (err) {
            console.error('Failed to start mic capture:', err);
        }
    }

    /**
     * Handle ICE candidate from server.
     */
    async _handleIceCandidate(candidate) {
        if (!this.pc || !candidate) return;

        try {
            await this.pc.addIceCandidate(new RTCIceCandidate(candidate));
        } catch (err) {
            console.warn('Failed to add ICE candidate:', err);
        }
    }

    /**
     * Handle barge-in clear — briefly mute remote audio.
     */
    _handleClear() {
        if (this.remoteAudio) {
            this.remoteAudio.muted = true;
            setTimeout(() => {
                if (this.remoteAudio) {
                    this.remoteAudio.muted = false;
                }
            }, 200);
        }
    }

    /**
     * Clean up all resources.
     */
    _cleanup() {
        // Stop mic capture
        if (this.scriptNode) {
            try { this.scriptNode.disconnect(); } catch (e) {}
            this.scriptNode = null;
        }
        if (this._silentGain) {
            try { this._silentGain.disconnect(); } catch (e) {}
            this._silentGain = null;
        }
        if (this.audioContext) {
            try { this.audioContext.close(); } catch (e) {}
            this.audioContext = null;
        }

        // Stop microphone
        if (this.mediaStream) {
            this.mediaStream.getTracks().forEach(t => t.stop());
            this.mediaStream = null;
        }

        // Close WebRTC peer connection
        if (this.pc) {
            try { this.pc.close(); } catch (e) {}
            this.pc = null;
        }

        // Remove audio element
        if (this.remoteAudio) {
            this.remoteAudio.srcObject = null;
            this.remoteAudio = null;
        }

        // Close signaling WebSocket
        if (this.ws) {
            try { this.ws.close(); } catch (e) {}
            this.ws = null;
        }
    }

    _setState(state) {
        this.state = state;
        this._emit('state', { state });
    }

    _emit(name, detail = {}) {
        this.dispatchEvent(new CustomEvent(name, { detail }));
    }
}

export default BrowserCall;
